import * as React from 'react'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Toolbar from '@mui/material/Toolbar'
import IconButton from '@mui/material/IconButton'
import Typography from '@mui/material/Typography'
import Menu from '@mui/material/Menu'
import MenuIcon from '@mui/icons-material/Menu'
import Avatar from '@mui/material/Avatar'
import Button from '@mui/material/Button'
import Tooltip from '@mui/material/Tooltip'
import MenuItem from '@mui/material/MenuItem'
import Divider from '@mui/material/Divider'
import Stack from '@mui/material/Stack'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'

import Link from '~/client/components/Link'
import NotificationsDropdown from '~/client/components/NotificationsDropdown'
import DataPalLogo from '~/client/components/DataPalLogo'
import CurrentUserAvatar from '~/client/components/CurrentUserAvatar'

const navLinks = [
  {title: 'About', href: '/about'},
  {title: 'Developer', href: '/developer'},
  {title: 'Help', href: '/help'},
]
const settings = [
  {title: 'Settings', href: '/settings'},
  {divider: true},
  {title: 'Sign out', href: '/signout'},
]


export default function TopNav({ currentUser, returnToOnLogin, ...props }) {
  const [anchorElNav, setAnchorElNav] = React.useState(null)
  const [anchorElUser, setAnchorElUser] = React.useState(null)

  const openNavMenu = React.useCallback(
    (event) => { setAnchorElNav(event.currentTarget) },
    [setAnchorElNav]
  )
  const openUserMenu = React.useCallback(
    (event) => { setAnchorElUser(event.currentTarget) },
    [setAnchorElNav]
  )
  const closeNavMenu = React.useCallback(
    () => { setAnchorElNav(null) },
    [setAnchorElNav]
  )
  const closeUserMenu = React.useCallback(
    () => { setAnchorElUser(null) },
    [setAnchorElNav]
  )

  const subProps = {
    anchorElNav,
    anchorElUser,
    openNavMenu,
    closeNavMenu,
    openUserMenu,
    closeUserMenu,
    currentUser,
    returnToOnLogin,
  }

  return (
    <AppBar {...props}  sx={{
      ...props.sx,
      backgroundColor: 'secondary.dark',
      height: 'var(--top-nav-height)'
    }}>
      <Toolbar>
        <BurgerMenu {...subProps}/>
        <DataPalLogoAndName />
        <NavLinks {...subProps}/>
        <UserControls {...subProps}/>
      </Toolbar>
    </AppBar>
  )
}

function DataPalLogoAndName(props){
  return <Stack direction="row" alignItems="center" sx={{
    mr: 1,
    flexGrow: { xs: 1, md: 0 },
    justifyContent: { xs: 'center', md: 'flex-start' },
  }}>
    <Link to="/">
      <DataPalLogo
        size={35}
        variant="white"
        sx={{position: 'relative', top: 3, left: -8, mr: 1}}
      />
    </Link>
    <Stack direction="column" alignItems="center" sx={{mr: 2}}>
      <Typography
        variant="h6"
        noWrap
        component={Link}
        to="/"
        sx={{
          fontWeight: 700,
          letterSpacing: '.3rem',
          color: 'inherit',
          textDecoration: 'none',
        }}
      >
        DataPal
      </Typography>
      <AlphaWarning/>
    </Stack>
  </Stack>
}

function BurgerMenu({
  anchorElNav,
  anchorElUser,
  openNavMenu,
  closeNavMenu,
  openUserMenu,
  closeUserMenu,
  currentUser,
}){
  return (
    <Box sx={{
      display: { xs: 'flex', md: 'none' },
      mr: 2,
      ml: -2,
    }}>
      <IconButton
        size="large"
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={openNavMenu}
        color="inherit"
      >
        <MenuIcon />
      </IconButton>
      <Menu
        id="menu-appbar"
        anchorEl={anchorElNav}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        open={Boolean(anchorElNav)}
        onClose={closeNavMenu}
        sx={{
          display: { xs: 'block', md: 'none' },
        }}
      >
        {navLinks.map(({ title, href, loggedIn }) => (
          loggedIn === true && !currentUser
            ? null
            : <MenuItem
              key={title}
              onClick={closeNavMenu}
              component={Link}
              to={href}
            >
              <Typography textAlign="center">{title}</Typography>
            </MenuItem>
        ))}
      </Menu>
    </Box>
  )
}

function NavLinks({
  closeNavMenu,
}){
  return <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
    {navLinks.map(({ title, href }) => (
      <Button
        key={title}
        sx={{
          my: 2, color: 'white', display: 'block',
          userSelect: 'none',
          WebkitUserDrag: 'none',
        }}
        onClick={closeNavMenu}
        component={Link}
        to={href}
      >
        {title}
      </Button>
    ))}
  </Box>
}

function UserControls({
  anchorElNav,
  anchorElUser,
  openNavMenu,
  closeNavMenu,
  openUserMenu,
  closeUserMenu,
  currentUser,
  returnToOnLogin,
}){
  if (currentUser) return (
    <Box sx={{ flexGrow: 0 }}>
      <Stack direction="row" alignItems="center">
        <NotificationsDropdown sx={{mr: 1}} />
        <IconButton onClick={openUserMenu} sx={{ p: 0, position: 'relative' }}>
          <CurrentUserAvatar sx={{mr: '3px'}}/>
          <ArrowDropDownIcon sx={{
            position: 'absolute',
            top: '10px',
            right: '-20px',
            opacity: '0.5',
          }}/>
        </IconButton>
      </Stack>
      <Menu
        sx={{ mt: '45px' }}
        id="menu-appbar"
        anchorEl={anchorElUser}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        keepMounted
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        open={Boolean(anchorElUser)}
        onClose={closeUserMenu}
      >
        <MenuItem disabled>
          {`Signed in as `}<br/>{currentUser.email}
        </MenuItem>
        {settings.map(({ divider, title, href, ...props }, index) => (
          divider
            ? <Divider key={index}/>
            : <MenuItem
              key={index}
              onClick={closeUserMenu}
              component={Link}
              to={href}
              {...props}
              sx={{
                userSelect: 'none',
                WebkitUserDrag: 'none',
              }}
            >
              <Typography textAlign="center">{title}</Typography>
            </MenuItem>
        ))}
      </Menu>
    </Box>
  )
  // logged out controls
  return (
    <Button
      variant="contained"
      component={Link}
      to="/signin"
      returnTo={!!returnToOnLogin}
      sx={{whiteSpace: 'nowrap',}}
    >
      <Box sx={{display: { xs: 'none', md: 'inline' }}}>
        Sign in or&nbsp;
      </Box>
      <span>Sign up</span>
    </Button>
  )
}


function AlphaWarning(){
  return <Typography
    variant="h7"
    component="div"
    sx={{
      color: 'orange',
      textShadow: '0 0 4px black',
      lineHeight: 1,
      fontSize: 'smaller',
      pointerEvents: 'none',
    }}
  >
    {'ALPHA'}
  </Typography>
}
